import DELETELATER_OVLocalCache from './DELETELATER_OVLocalCache';

export interface RefreshedTokens {
  access_token: string;
  refresh_token: string;
}

// How long the resolved refresh promise stays cached after settling, so that
// requests that failed slightly later (already in flight with the now-stale
// token) reuse it instead of triggering another /v2/authorize. The refresh
// itself starts immediately — there is no leading debounce delay.
const REFRESH_GRACE_MS = 1500;

let inFlightRefresh: Promise<RefreshedTokens> | null = null;

/**
 * Single-flight token refresh, shared across every transport (ui REST,
 * data-layer REST, Apollo GraphQL). When a burst of requests fails with a
 * token error at once, they all funnel through ONE /v2/authorize call and
 * reuse the freshly-minted token. The promise is briefly cached after settling
 * to also absorb stragglers, then cleared so future refreshes can run.
 */
export const refreshAccessToken = ({
  force = false,
}: { force?: boolean } = {}): Promise<RefreshedTokens> => {
  // `force` busts a grace-cached promise so we always mint a token NEWER than a
  // just-set stale stamp (used by the socket `access-updated` handler). Without
  // it, a refresh that resolved within the grace window — before the stamp —
  // would be reused and its token would still be considered stale.
  if (force) inFlightRefresh = null;

  if (!inFlightRefresh) {
    inFlightRefresh = (async () => {
      const refresh_token = await DELETELATER_OVLocalCache.get('refresh_token');
      const res = await fetch(`${process.env.OUTVIO_API_URL}/v2/authorize`, {
        method: 'POST',
        headers: new Headers({
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ grant_type: 'refresh_token', refresh_token }),
      });
      const data = await res.json();
      if (!data?.access_token) {
        throw new Error('TOKEN_REFRESH_FAILED');
      }
      await Promise.all([
        DELETELATER_OVLocalCache.set('token', data.access_token),
        DELETELATER_OVLocalCache.set('refresh_token', data.refresh_token),
      ]);
      return { access_token: data.access_token, refresh_token: data.refresh_token };
    })();

    // On success keep the promise for a short grace window (absorbs stragglers);
    // on failure clear immediately so a later request can retry.
    inFlightRefresh.then(
      () => {
        setTimeout(() => {
          inFlightRefresh = null;
        }, REFRESH_GRACE_MS);
      },
      () => {
        inFlightRefresh = null;
      },
    );
  }

  return inFlightRefresh;
};
